/**
 * NF C 15-100 rule pack (France, dwellings), after the 2024 revision (in force since
 * 23 August 2024, mandatory since 23 August 2025). Sources and open points:
 * docs/requirements/nfc15100.md. Every rule specific to France is `verified: false` until it
 * has been checked against the text of the standard and with installers or Consuel.
 */
import type { Element, Id } from '@elec/domain';
import { pointKind, type CircuitInfo } from '@elec/analysis';
import { elementBounds, getSymbol, ROOM_TYPES } from '@elec/symbols';
import {
  byId,
  circuitRcds,
  DEDICATED,
  earthRule,
  ids,
  MAX_RATING_A,
  name,
  NO_EARTH,
  rcdType,
  rule,
  section,
  sensitivityMa,
  variant,
  type Rule,
} from './core.ts';
import { BOARD_RULES } from './board.ts';
import { COMMON } from './common.ts';

/** RCDs of 30 mA or less upstream of a circuit. */
const rcd30 = (c: CircuitInfo) => c.rcds.filter((r) => sensitivityMa(r) <= 30);
/** Sockets on a circuit, each socket of a multiple box counted. */
const socketCount = (c: CircuitInfo) =>
  c.points.filter((p) => p.kind === 'socket').reduce((n, p) => n + (Number(p.element.props.count) || 1), 0);
/** Cooking hob and washing machine: type A or F (NF C 15-100, 2024). Names as said in France. */
const TYPE_A_LOADS = new Map<string, { fr: string; en: string; nl: string; de: string }>([
  ['iec.cooker', { fr: 'plaque de cuisson', en: 'hob', nl: 'kookplaat', de: 'Kochfeld' }],
  ['iec.washing-machine', { fr: 'lave-linge', en: 'washing machine', nl: 'wasmachine', de: 'Waschmaschine' }],
]);

const FR = byId([
  rule(
    {
      id: 'FR-RCD-00',
      severity: 'error',
      title: {
        fr: 'Circuit sans différentiel 30 mA',
        en: 'Circuit without a 30 mA RCD',
        nl: 'Groep zonder aardlekschakelaar van 30 mA',
        de: 'Stromkreis ohne FI 30 mA',
      },
      why: {
        fr: 'Dans un logement, tous les circuits sont protégés par un interrupteur différentiel 30 mA.',
        en: 'In a dwelling, every circuit is protected by a 30 mA RCD.',
        nl: 'In een woning wordt elke groep beveiligd door een aardlekschakelaar van 30 mA.',
        de: 'In einer Wohnung sind alle Stromkreise durch einen FI-Schutzschalter 30 mA geschützt.',
      },
      fix: {
        fr: 'Raccordez le circuit en aval d’un interrupteur différentiel 30 mA.',
        en: 'Connect the circuit downstream of a 30 mA RCD.',
        nl: 'Sluit de groep aan achter een aardlekschakelaar van 30 mA.',
        de: 'Schließen Sie den Stromkreis hinter einem FI-Schutzschalter 30 mA an.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits
        .filter((c) => rcd30(c).length === 0)
        .map((c) => {
          const s = c.rcds.length ? Math.min(...c.rcds.map(sensitivityMa)) : undefined;
          return {
            targets: ids(c),
            sheetId: c.sheet.id,
            message:
              s === undefined
                ? {
                    fr: `Circuit ${name(c)} : aucun différentiel en amont`,
                    en: `Circuit ${name(c)}: no RCD upstream`,
                    nl: `Groep ${name(c)}: geen aardlekschakelaar ervoor`,
                    de: `Stromkreis ${name(c)}: kein FI vorgeschaltet`,
                  }
                : {
                    fr: `Circuit ${name(c)} : différentiel ${s} mA, 30 mA requis`,
                    en: `Circuit ${name(c)}: RCD ${s} mA, 30 mA required`,
                    nl: `Groep ${name(c)}: aardlekschakelaar ${s} mA, 30 mA vereist`,
                    de: `Stromkreis ${name(c)}: FI ${s} mA, 30 mA vorgeschrieben`,
                  },
          };
        }),
  ),
  rule(
    {
      id: 'FR-RCD-01',
      severity: 'error',
      title: {
        fr: 'Moins de deux différentiels 30 mA, ou aucun de type A',
        en: 'Fewer than two 30 mA RCDs, or none of type A',
        nl: 'Minder dan twee aardlekschakelaars van 30 mA, of geen van type A',
        de: 'Weniger als zwei FI 30 mA, oder keiner vom Typ A',
      },
      why: {
        fr: 'Un logement compte au moins deux interrupteurs différentiels 30 mA, dont au moins un de type A, et les circuits sont répartis entre eux pour la continuité de service.',
        en: 'A dwelling has at least two 30 mA RCDs, at least one of them type A, with the circuits spread between them so a trip does not cut everything.',
        nl: 'Een woning heeft minstens twee aardlekschakelaars van 30 mA, waarvan minstens één van type A, met de groepen erover verdeeld zodat één uitschakeling niet alles uitschakelt.',
        de: 'Eine Wohnung hat mindestens zwei FI-Schutzschalter 30 mA, davon mindestens einen vom Typ A, und die Stromkreise sind für die Versorgungssicherheit auf sie verteilt.',
      },
      fix: {
        fr: 'Ajoutez un interrupteur différentiel 30 mA de type A et répartissez les circuits.',
        en: 'Add a type A 30 mA RCD and spread the circuits.',
        nl: 'Voeg een aardlekschakelaar van 30 mA type A toe en verdeel de groepen.',
        de: 'Fügen Sie einen FI-Schutzschalter 30 mA Typ A hinzu und verteilen Sie die Stromkreise.',
      },
      verified: false,
    },
    ({ a }) => {
      if (a.circuits.length < 2) return [];
      const rcds = circuitRcds(a).filter((r) => sensitivityMa(r) <= 30);
      const out: ReturnType<Rule['check']> = [];
      if (rcds.length < 2)
        out.push({
          targets: rcds.map((r) => r.id),
          message: {
            fr: `${rcds.length} différentiel(s) 30 mA pour ${a.circuits.length} circuits (min. 2)`,
            en: `${rcds.length} RCD(s) of 30 mA for ${a.circuits.length} circuits (min. 2)`,
            nl: `${rcds.length} aardlekschakelaar(s) van 30 mA voor ${a.circuits.length} groepen (min. 2)`,
            de: `${rcds.length} FI 30 mA für ${a.circuits.length} Stromkreise (min. 2)`,
          },
        });
      if (rcds.length && !rcds.some((r) => ['A', 'F', 'B'].includes(rcdType(r))))
        out.push({
          targets: rcds.map((r) => r.id),
          message: {
            fr: 'Aucun différentiel 30 mA de type A (ou F, B)',
            en: 'No 30 mA RCD of type A (or F, B)',
            nl: 'Geen aardlekschakelaar van 30 mA type A (of F, B)',
            de: 'Kein FI 30 mA vom Typ A (oder F, B)',
          },
        });
      return out;
    },
  ),
  rule(
    {
      id: 'FR-RCD-02',
      severity: 'error',
      title: {
        fr: 'Plus de 8 circuits sous un différentiel',
        en: 'More than 8 circuits under one RCD',
        nl: 'Meer dan 8 groepen achter één aardlekschakelaar',
        de: 'Mehr als 8 Stromkreise hinter einem FI',
      },
      why: {
        fr: 'Un interrupteur différentiel protège au plus 8 circuits.',
        en: 'An RCD protects at most 8 circuits.',
        nl: 'Een aardlekschakelaar beveiligt hoogstens 8 groepen.',
        de: 'Ein FI-Schutzschalter schützt höchstens 8 Stromkreise.',
      },
      fix: {
        fr: 'Ajoutez un interrupteur différentiel et répartissez les circuits.',
        en: 'Add an RCD and spread the circuits.',
        nl: 'Voeg een aardlekschakelaar toe en verdeel de groepen.',
        de: 'Fügen Sie einen FI-Schutzschalter hinzu und verteilen Sie die Stromkreise.',
      },
      verified: false,
    },
    ({ a, project }) =>
      [...a.byRcd].flatMap(([id, list]) => {
        const n = list.filter((c) => c.rcds[0]?.id === id).length;
        const r = project.elements[id];
        if (!r || n <= 8) return [];
        return [
          {
            targets: [id],
            sheetId: r.sheetId,
            message: {
              fr: `${r.tag} : ${n} circuits (max. 8)`,
              en: `${r.tag}: ${n} circuits (max. 8)`,
              nl: `${r.tag}: ${n} groepen (max. 8)`,
              de: `${r.tag}: ${n} Stromkreise (max. 8)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'FR-RCD-03',
      severity: 'error',
      title: {
        fr: 'Plaque de cuisson ou lave-linge sans différentiel de type A',
        en: 'Hob or washing machine without a type A RCD',
        nl: 'Kookplaat of wasmachine zonder aardlekschakelaar type A',
        de: 'Kochfeld oder Waschmaschine ohne FI vom Typ A',
      },
      why: {
        fr: 'Les circuits de la plaque de cuisson et du lave-linge sont protégés par un différentiel 30 mA de type A ou F.',
        en: 'The hob and washing machine circuits are protected by a 30 mA RCD of type A or F.',
        nl: 'De groepen van de kookplaat en de wasmachine worden beveiligd door een aardlekschakelaar van 30 mA type A of F.',
        de: 'Die Stromkreise des Kochfelds und der Waschmaschine sind durch einen FI 30 mA vom Typ A oder F geschützt.',
      },
      fix: {
        fr: 'Placez ces circuits sous un interrupteur différentiel de type A (ou F).',
        en: 'Put these circuits under a type A (or F) RCD.',
        nl: 'Plaats deze groepen achter een aardlekschakelaar type A (of F).',
        de: 'Legen Sie diese Stromkreise hinter einen FI-Schutzschalter vom Typ A (oder F).',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const load = c.points.find((p) => TYPE_A_LOADS.has(p.element.symbolId));
        const rs = rcd30(c);
        if (!load || !rs.length || rs.some((r) => ['A', 'F', 'B'].includes(rcdType(r)))) return [];
        const what = TYPE_A_LOADS.get(load.element.symbolId)!;
        return [
          {
            targets: [c.device.id, ...rs.map((r) => r.id)],
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} (${what.fr}) : différentiel de type AC`,
              en: `Circuit ${name(c)} (${what.en}): type AC RCD`,
              nl: `Groep ${name(c)} (${what.nl}): aardlekschakelaar type AC`,
              de: `Stromkreis ${name(c)} (${what.de}): FI vom Typ AC`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'FR-CIR-01',
      severity: 'error',
      title: {
        fr: 'Trop de prises sur un circuit',
        en: 'Too many sockets on a circuit',
        nl: 'Te veel wandcontactdozen op een groep',
        de: 'Zu viele Steckdosen an einem Stromkreis',
      },
      why: {
        fr: 'Au plus 8 prises par circuit en 1,5 mm² (disjoncteur 16 A) et 12 prises en 2,5 mm² (disjoncteur 20 A). Chaque prise d’un socle multiple compte.',
        en: 'At most 8 sockets per circuit in 1.5 mm² (16 A breaker) and 12 in 2.5 mm² (20 A breaker). Every socket of a multiple box counts.',
        nl: 'Hoogstens 8 wandcontactdozen per groep in 1,5 mm² (automaat 16 A) en 12 in 2,5 mm² (automaat 20 A). Elke contactdoos van een meervoudige doos telt.',
        de: 'Höchstens 8 Steckdosen je Stromkreis in 1,5 mm² (LS 16 A) und 12 Steckdosen in 2,5 mm² (LS 20 A). Jede Steckdose einer Mehrfachdose zählt.',
      },
      fix: {
        fr: 'Scindez le circuit, ou passez en 2,5 mm² avec un disjoncteur 20 A.',
        en: 'Split the circuit, or move to 2.5 mm² with a 20 A breaker.',
        nl: 'Splits de groep, of ga naar 2,5 mm² met een automaat van 20 A.',
        de: 'Teilen Sie den Stromkreis auf, oder gehen Sie auf 2,5 mm² mit einem LS 20 A über.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const n = socketCount(c);
        const s = c.cable.sectionMm2;
        if (!n || !(s > 0)) return [];
        const max = s < 2.5 ? 8 : 12;
        if (n <= max) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${n} prises en ${section(c)} mm² (max. ${max})`,
              en: `Circuit ${name(c)}: ${n} sockets in ${c.cable.sectionMm2} mm² (max. ${max})`,
              nl: `Groep ${name(c)}: ${n} wandcontactdozen in ${section(c)} mm² (max. ${max})`,
              de: `Stromkreis ${name(c)}: ${n} Steckdosen in ${section(c)} mm² (max. ${max})`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'FR-CIR-02',
      severity: 'error',
      title: {
        fr: 'Plus de 8 points lumineux sur un circuit',
        en: 'More than 8 lighting points on a circuit',
        nl: 'Meer dan 8 lichtpunten op een groep',
        de: 'Mehr als 8 Lichtpunkte an einem Stromkreis',
      },
      why: {
        fr: 'Un circuit d’éclairage alimente au plus 8 points lumineux (les prises commandées comptent comme points lumineux).',
        en: 'A lighting circuit feeds at most 8 lighting points (switched sockets count as lighting points).',
        nl: 'Een verlichtingsgroep voedt hoogstens 8 lichtpunten (geschakelde contactdozen tellen als lichtpunt).',
        de: 'Ein Beleuchtungsstromkreis versorgt höchstens 8 Lichtpunkte (geschaltete Steckdosen zählen als Lichtpunkte).',
      },
      fix: {
        fr: 'Scindez le circuit d’éclairage en deux.',
        en: 'Split the lighting circuit in two.',
        nl: 'Splits de verlichtingsgroep in twee.',
        de: 'Teilen Sie den Beleuchtungsstromkreis in zwei auf.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const n = c.points.filter((p) => p.kind === 'light').length;
        if (n <= 8) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${n} points lumineux (max. 8)`,
              en: `Circuit ${name(c)}: ${n} lighting points (max. 8)`,
              nl: `Groep ${name(c)}: ${n} lichtpunten (max. 8)`,
              de: `Stromkreis ${name(c)}: ${n} Lichtpunkte (max. 8)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'FR-CIR-03',
      severity: 'error',
      title: {
        fr: 'Calibre trop élevé pour la section',
        en: 'Rating too high for the cable section',
        nl: 'Nominale stroom te hoog voor de doorsnede',
        de: 'Bemessungsstrom zu hoch für den Querschnitt',
      },
      why: {
        fr: 'Calibre maximal du disjoncteur selon la section (cuivre) : 1,5 mm² → 16 A, 2,5 mm² → 20 A, 4 mm² → 25 A, 6 mm² → 32 A.',
        en: 'Maximum breaker rating per section (copper): 1.5 mm² → 16 A, 2.5 mm² → 20 A, 4 mm² → 25 A, 6 mm² → 32 A.',
        nl: 'Maximale nominale stroom van de automaat per doorsnede (koper): 1,5 mm² → 16 A, 2,5 mm² → 20 A, 4 mm² → 25 A, 6 mm² → 32 A.',
        de: 'Maximaler Bemessungsstrom des LS je nach Querschnitt (Kupfer): 1,5 mm² → 16 A, 2,5 mm² → 20 A, 4 mm² → 25 A, 6 mm² → 32 A.',
      },
      fix: {
        fr: 'Réduisez le calibre ou augmentez la section.',
        en: 'Lower the rating or increase the section.',
        nl: 'Verlaag de nominale stroom of vergroot de doorsnede.',
        de: 'Verringern Sie den Bemessungsstrom oder vergrößern Sie den Querschnitt.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const max = MAX_RATING_A[String(c.cable.sectionMm2)];
        if (!max || c.ratingA === undefined || c.ratingA <= max) return [];
        return [
          {
            targets: ids(c),
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${c.ratingA} A en ${section(c)} mm² (max. ${max} A)`,
              en: `Circuit ${name(c)}: ${c.ratingA} A on ${c.cable.sectionMm2} mm² (max. ${max} A)`,
              nl: `Groep ${name(c)}: ${c.ratingA} A op ${section(c)} mm² (max. ${max} A)`,
              de: `Stromkreis ${name(c)}: ${c.ratingA} A auf ${section(c)} mm² (max. ${max} A)`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'FR-CIR-04',
      severity: 'error',
      title: {
        fr: 'Appareil sans circuit spécialisé',
        en: 'Appliance without a dedicated circuit',
        nl: 'Toestel zonder eigen groep',
        de: 'Gerät ohne eigenen Stromkreis',
      },
      why: {
        fr: 'Plaque de cuisson, four, lave-linge, sèche-linge, lave-vaisselle, chauffe-eau, borne de recharge et pompe à chaleur ont chacun leur circuit spécialisé.',
        en: 'Hob, oven, washing machine, dryer, dishwasher, water heater, EV charger and heat pump each have a dedicated circuit.',
        nl: 'Kookplaat, oven, wasmachine, droger, vaatwasser, boiler, laadpunt en warmtepomp hebben elk een eigen groep.',
        de: 'Kochfeld, Backofen, Waschmaschine, Wäschetrockner, Geschirrspüler, Warmwasserbereiter, Ladestation und Wärmepumpe haben jeweils einen eigenen Stromkreis.',
      },
      fix: {
        fr: 'Placez l’appareil seul sur un circuit.',
        en: 'Put the appliance alone on a circuit.',
        nl: 'Plaats het toestel alleen op een groep.',
        de: 'Schließen Sie das Gerät allein an einen Stromkreis an.',
      },
      verified: false,
    },
    ({ a }) =>
      a.circuits.flatMap((c) => {
        const big = c.points.filter((p) => DEDICATED.has(p.element.symbolId));
        if (!big.length || c.points.length === 1) return [];
        return [
          {
            targets: [c.device.id, ...big.map((p) => p.element.id)],
            sheetId: c.sheet.id,
            message: {
              fr: `Circuit ${name(c)} : ${big.map((p) => p.element.tag).join(', ')} partage le circuit`,
              en: `Circuit ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} shares the circuit`,
              nl: `Groep ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} deelt de groep`,
              de: `Stromkreis ${name(c)}: ${big.map((p) => p.element.tag).join(', ')} teilt den Stromkreis`,
            },
          },
        ];
      }),
  ),
  rule(
    {
      id: 'FR-CIR-05',
      severity: 'error',
      title: {
        fr: 'Prise sans contact de terre',
        en: 'Socket without earth contact',
        nl: 'Wandcontactdoos zonder randaarde',
        de: 'Steckdose ohne Schutzkontakt',
      },
      why: {
        fr: 'Les prises de courant sont de type 2P+T, raccordées au conducteur de protection.',
        en: 'Socket outlets are 2P+E, connected to the protective conductor.',
        nl: 'Wandcontactdozen zijn van het type 2P+A, aangesloten op de beschermingsleiding.',
        de: 'Steckdosen sind vom Typ 2P+E und an den Schutzleiter angeschlossen.',
      },
      fix: {
        fr: 'Remplacez par une prise 2P+T.',
        en: 'Replace with a 2P+E socket.',
        nl: 'Vervang door een wandcontactdoos met randaarde.',
        de: 'Ersetzen Sie sie durch eine Steckdose 2P+E.',
      },
      verified: false,
    },
    ({ project }) =>
      Object.values(project.elements)
        .filter((e) => NO_EARTH.has(e.symbolId) && project.sheets[e.sheetId]?.kind === 'single-line')
        .map((e) => ({
          targets: [e.id],
          sheetId: e.sheetId,
          message: {
            fr: `${e.tag || 'Prise'} : sans terre`,
            en: `${e.tag || 'Socket'}: no earth`,
            nl: `${e.tag || 'Wandcontactdoos'}: geen randaarde`,
            de: `${e.tag || 'Steckdose'}: ohne Schutzleiter`,
          },
        })),
  ),
  rule(
    {
      id: 'FR-CIR-06',
      severity: 'warning',
      title: {
        fr: 'Un seul circuit d’éclairage',
        en: 'Only one lighting circuit',
        nl: 'Slechts één verlichtingsgroep',
        de: 'Nur ein Beleuchtungsstromkreis',
      },
      why: {
        fr: 'Un logement compte au moins deux circuits d’éclairage ; un seul est admis pour un studio ou un T1.',
        en: 'A dwelling has at least two lighting circuits; one is allowed for a studio or one-room flat.',
        nl: 'Een woning heeft minstens twee verlichtingsgroepen; één is toegelaten voor een studio of eenkamerappartement.',
        de: 'Eine Wohnung hat mindestens zwei Beleuchtungsstromkreise; einer ist für ein Studio oder eine Einzimmerwohnung zulässig.',
      },
      fix: {
        fr: 'Répartissez les points lumineux sur deux circuits (sauf studio ou T1).',
        en: 'Spread the lighting points over two circuits (except a studio or one-room flat).',
        nl: 'Verdeel de lichtpunten over twee groepen (behalve een studio of eenkamerappartement).',
        de: 'Verteilen Sie die Lichtpunkte auf zwei Stromkreise (außer bei einem Studio oder einer Einzimmerwohnung).',
      },
      verified: false,
    },
    ({ a }) => {
      const lighting = a.circuits.filter((c) => c.points.some((p) => p.kind === 'light'));
      if (lighting.length !== 1) return [];
      const c = lighting[0]!;
      return [
        {
          targets: ids(c),
          sheetId: c.sheet.id,
          message: {
            fr: `Un seul circuit d’éclairage (${name(c)})`,
            en: `Only one lighting circuit (${name(c)})`,
            nl: `Slechts één verlichtingsgroep (${name(c)})`,
            de: `Nur ein Beleuchtungsstromkreis (${name(c)})`,
          },
        },
      ];
    },
  ),
  rule(
    {
      id: 'FR-ROOM-01',
      severity: 'warning',
      title: {
        fr: 'Pas assez de prises dans la pièce',
        en: 'Not enough sockets in the room',
        nl: 'Te weinig wandcontactdozen in de ruimte',
        de: 'Zu wenige Steckdosen im Raum',
      },
      why: {
        fr: 'Nombre minimal de prises 2P+T : séjour 1 par 4 m² (min. 5, 7 au-delà de 28 m²), chambre 3, cuisine 6 (3 si 4 m² ou moins). Chaque prise d’un socle multiple compte.',
        en: 'Minimum 2P+E sockets: living room 1 per 4 m² (min. 5, 7 above 28 m²), bedroom 3, kitchen 6 (3 if 4 m² or less). Every socket of a multiple box counts.',
        nl: 'Minimum aantal wandcontactdozen: woonkamer 1 per 4 m² (min. 5, 7 boven 28 m²), slaapkamer 3, keuken 6 (3 bij 4 m² of minder). Elke contactdoos van een meervoudige doos telt.',
        de: 'Mindestanzahl Steckdosen 2P+E: Wohnzimmer 1 je 4 m² (min. 5, 7 über 28 m²), Schlafzimmer 3, Küche 6 (3 bei 4 m² oder weniger). Jede Steckdose einer Mehrfachdose zählt.',
      },
      fix: {
        fr: 'Ajoutez des prises dans la pièce, sur le plan et au schéma.',
        en: 'Add sockets in the room, on the plan and in the diagram.',
        nl: 'Voeg wandcontactdozen toe in de ruimte, op het plan en in het schema.',
        de: 'Fügen Sie Steckdosen im Raum hinzu, im Plan und im Schema.',
      },
      verified: false,
    },
    ({ project, a, planSheets }) => {
      // Sockets per code, as counted on the single-line diagram (a double socket is 2).
      const perTag = new Map<string, number>();
      for (const c of a.circuits)
        for (const p of c.points)
          if (p.kind === 'socket' && p.element.tag)
            perTag.set(p.element.tag, Number(p.element.props.count) || 1);
      const roomDef = getSymbol('plan.room')!;
      const sockets = Object.values(project.elements).filter(
        (e) => planSheets.includes(e.sheetId) && pointKind(e) === 'socket',
      );
      const out: ReturnType<Rule['check']> = [];
      for (const r of Object.values(project.elements)) {
        if (!planSheets.includes(r.sheetId) || r.symbolId !== 'plan.room') continue;
        const type = String(r.props.roomType ?? 'other');
        if (type !== 'living' && type !== 'bedroom' && type !== 'kitchen') continue;
        const rb = elementBounds(roomDef, r);
        const scale = project.sheets[r.sheetId]?.planScale;
        const area = scale ? ((rb.maxX - rb.minX) * scale * (rb.maxY - rb.minY) * scale) / 1e6 : undefined;
        const min =
          type === 'bedroom'
            ? 3
            : type === 'kitchen'
              ? area !== undefined && area <= 4
                ? 3
                : 6
              : area === undefined
                ? 5
                : area > 28
                  ? 7
                  : Math.max(5, Math.ceil(area / 4));
        const inside = sockets.filter((e: Element) => {
          if (e.sheetId !== r.sheetId) return false;
          const b = elementBounds(getSymbol(e.symbolId)!, e);
          const cx = (b.minX + b.maxX) / 2;
          const cy = (b.minY + b.maxY) / 2;
          return cx >= rb.minX && cx <= rb.maxX && cy >= rb.minY && cy <= rb.maxY;
        });
        const n = inside.reduce((s, e) => s + (perTag.get(e.tag) ?? (Number(e.props.count) || 1)), 0);
        if (n >= min) continue;
        const label = ROOM_TYPES.find((x) => x.value === type)!.label;
        const nm = String(r.props.name ?? '').trim();
        const room = (l: 'fr' | 'en' | 'nl') =>
          !nm || nm === 'Pièce' || nm.toLowerCase() === label[l].toLowerCase()
            ? label[l]
            : l === 'fr'
              ? `${label.fr} « ${nm} »`
              : `${label[l]} “${nm}”`;
        out.push({
          targets: [r.id, ...inside.map((e) => e.id)] as Id[],
          sheetId: r.sheetId,
          message: {
            fr: `${room('fr')} : ${n} prise(s) (min. ${min})`,
            en: `${room('en')}: ${n} socket(s) (min. ${min})`,
            nl: `${room('nl')}: ${n} ${n === 1 ? 'wandcontactdoos' : 'wandcontactdozen'} (min. ${min})`,
            de: `${room('fr') === label.fr ? label.fr : `${label.fr} „${nm}“`}: ${n} ${n === 1 ? 'Steckdose' : 'Steckdosen'} (min. ${min})`,
          },
        });
      }
      return out;
    },
  ),
  earthRule('FR-EARTH-01', {
    why: {
      fr: 'En schéma TT, la prise de terre figure au schéma et sa résistance respecte R × IΔn ≤ 50 V, avec le différentiel le moins sensible (500 mA pour le disjoncteur de branchement : 100 Ω).',
      en: 'In a TT system the earth electrode appears on the diagram and its resistance meets R × IΔn ≤ 50 V, with the least sensitive RCD (500 mA for the DSO’s branch breaker: 100 Ω).',
      nl: 'In een TT-stelsel staat de aardelektrode op het schema en voldoet haar weerstand aan R × IΔn ≤ 50 V, met de minst gevoelige aardlekschakelaar (500 mA voor de aansluitautomaat: 100 Ω).',
      de: 'Im TT-System steht der Erder im Schema und sein Widerstand erfüllt R × IΔn ≤ 50 V, mit dem am wenigsten empfindlichen FI (500 mA für den Hausanschluss-Leistungsschalter: 100 Ω).',
    },
    fix: {
      fr: 'Sans prise de terre au schéma : bouton « Ajouter la prise de terre » ci-dessous, ou cherchez « terre » dans la palette. Saisissez ensuite la valeur mesurée (Contrôle › Mesures et essais). Trop élevée : améliorez la prise de terre (piquet supplémentaire, connexions) et mesurez à nouveau.',
      en: 'With no electrode on the diagram: the “Add the earth electrode” button below, or search “earth” in the palette. Then enter the measured value (Inspection › Test results). Too high: improve the electrode (another rod, its connections) and measure again.',
      nl: 'Zonder aardelektrode op het schema: de knop “Aardverbinding toevoegen” hieronder, of zoek “aard” in het palet. Vul daarna de gemeten waarde in (Keuring › Metingen en testen). Te hoog: verbeter de aarding (extra aardpen, verbindingen) en meet opnieuw.',
      de: 'Ohne Erder im Schema: Schaltfläche „Erder hinzufügen“ unten, oder suchen Sie „Erde“ im Symbolbereich. Geben Sie danach den gemessenen Wert ein (Kontrolle › Messergebnisse). Zu hoch: verbessern Sie den Erder (zusätzlicher Erdspieß, Verbindungen) und messen Sie erneut.',
    },
    verified: false,
  }),
]);

const PLAN = variant(COMMON['R-DOC-03']!, {
  severity: 'warning',
  why: {
    fr: 'Quand un plan est joint, chaque point du schéma y figure avec le même repère, et inversement.',
    en: 'When a plan is included, every point of the diagram appears on it with the same code, and vice versa.',
    nl: 'Als er een plan is, staat elk punt van het schema erop met dezelfde code, en omgekeerd.',
    de: 'Wenn ein Plan beiliegt, steht jeder Punkt des Schemas mit demselben Kennzeichen darin, und umgekehrt.',
  },
});
const ROOMS = variant(COMMON['R-PLAN-01']!, {
  why: {
    fr: 'La pièce d’un point sert à vérifier le nombre minimal de prises par pièce.',
    en: 'A point’s room is used to check the minimum number of sockets per room.',
    nl: 'De ruimte van een punt dient om het minimum aantal wandcontactdozen per ruimte te controleren.',
    de: 'Der Raum eines Punktes dient zur Prüfung der Mindestanzahl Steckdosen je Raum.',
  },
});
const DROP = variant(COMMON['R-CALC-03']!, {
  why: {
    fr: 'Chute de tension maximale depuis l’origine (réseau public BT) : 3 % pour l’éclairage, 5 % pour les autres usages (NF C 15-100, 525).',
    en: 'Maximum voltage drop from the origin (public LV supply): 3 % for lighting, 5 % for other uses (NF C 15-100, 525).',
    nl: 'Maximale spanningsval vanaf de oorsprong (openbaar laagspanningsnet): 3 % voor verlichting, 5 % voor andere toepassingen (NF C 15-100, 525).',
    de: 'Maximaler Spannungsfall ab dem Anfang der Installation (öffentliches NS-Netz): 3 % für Beleuchtung, 5 % für andere Verwendungen (NF C 15-100, 525).',
  },
  verified: true,
});

/** R-BOARD-01 outside Belgium: the same check, without the RGIE article. */
const BOARD_GENERIC = variant(BOARD_RULES['R-BOARD-01']!, {
  why: {
    fr: 'Les emplacements libres du tableau doivent être fermés par des obturateurs : sans eux, des parties sous tension restent accessibles (degré de protection IP2X de l’enveloppe, IEC 60364-4-41).',
    en: 'Free spaces in the board must be closed with blanking plates: without them, live parts can be touched (IP2X enclosure, IEC 60364-4-41).',
    nl: 'Vrije plaatsen in het bord moeten afgesloten zijn met afdekplaatjes: zonder ze blijven delen onder spanning bereikbaar (beschermingsgraad IP2X van de omhulling, IEC 60364-4-41).',
    de: 'Freie Plätze im Verteiler müssen mit Abdeckungen verschlossen sein: ohne sie bleiben spannungsführende Teile berührbar (Schutzart IP2X des Gehäuses, IEC 60364-4-41).',
  },
});

/** The NF C 15-100 pack, in the order findings are listed. */
export const NFC15100_RULES: Rule[] = [
  COMMON['R-DOC-02']!,
  FR['FR-RCD-00']!,
  FR['FR-RCD-01']!,
  FR['FR-RCD-02']!,
  FR['FR-RCD-03']!,
  FR['FR-CIR-01']!,
  FR['FR-CIR-02']!,
  FR['FR-CIR-03']!,
  FR['FR-CIR-04']!,
  COMMON['R-CIR-04']!,
  COMMON['R-DEV-01']!,
  FR['FR-CIR-05']!,
  COMMON['R-CIR-06']!,
  COMMON['R-CIR-07']!,
  FR['FR-CIR-06']!,
  COMMON['R-DOC-01']!,
  PLAN,
  COMMON['R-TOPO-01']!,
  COMMON['R-TOPO-02']!,
  FR['FR-EARTH-01']!,
  FR['FR-ROOM-01']!,
  ROOMS,
  BOARD_GENERIC,
  BOARD_RULES['R-SEL-01']!,
  BOARD_RULES['R-SEL-02']!,
  COMMON['R-CTL-01']!,
  COMMON['R-CTL-02']!,
  COMMON['R-CTL-03']!,
  COMMON['R-DOC-04']!,
  COMMON['R-CALC-01']!,
  COMMON['R-CALC-02']!,
  DROP,
  COMMON['R-CALC-04']!,
  COMMON['R-CALC-05']!,
  BOARD_RULES['R-CALC-06']!,
  COMMON['R-CALC-00']!,
  COMMON['R-ML-01']!,
  COMMON['R-SRV-01']!,
];
